import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router';
import { PublicLayout } from '@/components/layout/public-layout';
import { AuthLayout } from '@/components/layout/auth-layout';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { RequireAuth } from '@/lib/auth/require-auth';
import { RequireRole } from '@/lib/auth/require-role';
import { LoadingScreen } from '@/components/shared/loading-screen';
import { ErrorState } from '@/components/shared/error-state';

// ---------------------------------------------------------------------------
// Lazy-loaded pages
// ---------------------------------------------------------------------------

const LandingPage = lazy(() =>
  import('@/features/auth/pages/landing-page').then((m) => ({ default: m.LandingPage })),
);
const DesignSystemPage = lazy(() =>
  import('@/features/design-system/pages/design-system-page').then((m) => ({
    default: m.DesignSystemPage,
  })),
);
const LoginPage = lazy(() =>
  import('@/features/auth/pages/login-page').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/features/auth/pages/register-page').then((m) => ({ default: m.RegisterPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/forgot-password-page').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/pages/reset-password-page').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const VerifyEmailPage = lazy(() =>
  import('@/features/auth/pages/verify-email-page').then((m) => ({
    default: m.VerifyEmailPage,
  })),
);
const NotFoundPage = lazy(() =>
  import('@/features/auth/pages/not-found-page').then((m) => ({
    default: m.NotFoundPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/dashboard-page').then((m) => ({
    default: m.DashboardPage,
  })),
);
const MarketsPage = lazy(() =>
  import('@/features/predictions/pages/markets-page').then((m) => ({
    default: m.MarketsPage,
  })),
);
const LiveArenaPage = lazy(() =>
  import('@/features/predictions/pages/live-arena-page').then((m) => ({
    default: m.LiveArenaPage,
  })),
);
const MyPredictionsPage = lazy(() =>
  import('@/features/predictions/pages/my-predictions-page').then((m) => ({
    default: m.MyPredictionsPage,
  })),
);
const LeaderboardPage = lazy(() =>
  import('@/features/predictions/pages/leaderboard-page').then((m) => ({
    default: m.LeaderboardPage,
  })),
);
const ProfilePage = lazy(() =>
  import('@/features/settings/pages/profile-page').then((m) => ({
    default: m.ProfilePage,
  })),
);
const UsersPage = lazy(() =>
  import('@/features/users/pages/users-page').then((m) => ({
    default: m.UsersPage,
  })),
);
const AdminLayout = lazy(() =>
  import('@/features/admin/components/admin-layout').then((m) => ({
    default: m.AdminLayout,
  })),
);
const AdminDashboardPage = lazy(() =>
  import('@/features/admin/pages/admin-dashboard-page').then((m) => ({
    default: m.AdminDashboardPage,
  })),
);

// ---------------------------------------------------------------------------
// Error boundary element
// ---------------------------------------------------------------------------

function RouteError() {
  return (
    <ErrorState
      title="Something went wrong"
      message="An unexpected error occurred while loading this page."
      onRetry={() => window.location.reload()}
    />
  );
}

// ---------------------------------------------------------------------------
// Suspense wrapper
// ---------------------------------------------------------------------------

function SuspenseFallback({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingScreen />}>{children}</Suspense>;
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const router = createBrowserRouter([
  // Standalone, sample-data admin layout preview. Authentication is unchanged.
  {
    path: 'admin',
    element: (
      <SuspenseFallback>
        <AdminLayout />
      </SuspenseFallback>
    ),
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        element: (
          <SuspenseFallback>
            <AdminDashboardPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <SuspenseFallback>
            <AdminDashboardPage />
          </SuspenseFallback>
        ),
      },
    ],
  },
  // Public pages
  {
    element: <PublicLayout />,
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        element: (
          <SuspenseFallback>
            <LandingPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'design-system',
        element: (
          <SuspenseFallback>
            <DesignSystemPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'verify-email',
        element: (
          <SuspenseFallback>
            <VerifyEmailPage />
          </SuspenseFallback>
        ),
      },
    ],
  },

  // Auth pages (redirect away if already logged in)
  {
    element: <AuthLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: 'login',
        element: (
          <SuspenseFallback>
            <LoginPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'register',
        element: (
          <SuspenseFallback>
            <RegisterPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <SuspenseFallback>
            <ForgotPasswordPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <SuspenseFallback>
            <ResetPasswordPage />
          </SuspenseFallback>
        ),
      },
    ],
  },

  // Protected prediction app pages
  {
    element: (
      <RequireAuth>
        <DashboardLayout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      {
        path: 'dashboard',
        element: (
          <SuspenseFallback>
            <DashboardPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'markets',
        element: (
          <SuspenseFallback>
            <MarketsPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'arena',
        element: (
          <SuspenseFallback>
            <LiveArenaPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'my-predictions',
        element: (
          <SuspenseFallback>
            <MyPredictionsPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'leaderboard',
        element: (
          <SuspenseFallback>
            <LeaderboardPage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'settings/profile',
        element: (
          <SuspenseFallback>
            <ProfilePage />
          </SuspenseFallback>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <RequireRole role="ADMIN">
            <SuspenseFallback>
              <UsersPage />
            </SuspenseFallback>
          </RequireRole>
        ),
      },
    ],
  },

  // 404 catch-all
  {
    path: '*',
    element: (
      <SuspenseFallback>
        <NotFoundPage />
      </SuspenseFallback>
    ),
  },
]);
